import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useOrganization } from '../../../contexts/OrganizationContext';
import customerService from '../../../utils/customerService';
import dynamicOptionsService from '../../../utils/dynamicOptionsService';

const CustomerFilters = ({ onFiltersChange, activeFilters }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: activeFilters?.status || '',
    serviceType: activeFilters?.serviceType || '',
    location: activeFilters?.location || '',
    dateRange: activeFilters?.dateRange || '',
    customerTier: activeFilters?.customerTier || '',
    pipelineStage: activeFilters?.pipelineStage || '',
    customerType: activeFilters?.customerType || '',
    leadSource: activeFilters?.leadSource || '',
    ...activeFilters
  });

  // Dynamic data states
  const [pipelineStages, setPipelineStages] = useState([]);
  const [primaryServices, setPrimaryServices] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  
  const { currentOrganization } = useOrganization();

  // Load dynamic filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      if (!currentOrganization?.id) return;
      
      try {
        setLoadingOptions(true);
        
        // Load pipeline stages
        const stages = await customerService.getPipelineStages(currentOrganization.id);
        setPipelineStages(stages.sort((a, b) => a.position - b.position));
        
        // Load primary services
        const services = await dynamicOptionsService.getPrimaryServices(currentOrganization.id);
        setPrimaryServices(services);
        
        // Load lead sources
        const sources = await dynamicOptionsService.getLeadSources(currentOrganization.id);
        setLeadSources(sources);
        
      } catch (error) {
        console.error('Error loading filter options:', error);
        // Set fallback options if loading fails
        setPipelineStages([]);
        setPrimaryServices([]);
        setLeadSources([]);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadFilterOptions();
  }, [currentOrganization?.id]);

  // Static filter options
  const statusOptions = [
    { value: '', label: 'All Customers' },
    { value: 'active', label: 'Active Only' },
    { value: 'archived', label: 'Archived Only' }
  ];

  const customerTierOptions = [
    { value: '', label: 'All Tiers' },
    { value: 'Premium', label: 'Premium' },
    { value: 'Standard', label: 'Standard' },
    { value: 'Basic', label: 'Basic' }
  ];

  const customerTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'individual', label: 'Individual' },
    { value: 'organization', label: 'Organization' }
  ];

  const dateRangeOptions = [
    { value: '', label: 'All Time' },
    { value: 'last-7-days', label: 'Last 7 Days' },
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'last-90-days', label: 'Last 90 Days' },
    { value: 'this-month', label: 'This Month' },
    { value: 'last-month', label: 'Last Month' },
    { value: 'last-3-months', label: 'Last 3 Months' }
  ];

  // Dynamic filter options
  const pipelineStageOptions = [
    { value: '', label: 'All Stages' },
    ...pipelineStages.map(stage => ({
      value: stage.name,
      label: stage.name
    }))
  ];

  const serviceTypeOptions = [
    { value: '', label: 'All Services' },
    ...primaryServices.map(service => ({
      value: service.name,
      label: service.name
    }))
  ];

  const leadSourceOptions = [
    { value: '', label: 'All Sources' },
    ...leadSources.map(source => ({
      value: source.name,
      label: source.name
    }))
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      status: '',
      serviceType: '',
      location: '',
      dateRange: '',
      customerTier: '',
      pipelineStage: '',
      customerType: '',
      leadSource: ''
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2"
          >
            <Icon name="Filter" size={16} />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="bg-primary text-white text-xs rounded-full px-2 py-0.5 ml-2">
                {Object.values(filters).filter(value => value !== '').length}
              </span>
            )}
          </Button>
          
          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearFilters} className="text-sm">
              Clear All
            </Button>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            <Icon name="Download" size={16} className="mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm">
            <Icon name="Upload" size={16} className="mr-2" />
            Import
          </Button>
        </div>
      </div>

      {showFilters && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Pipeline Stage Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Pipeline Stage</label>
              <select
                value={filters.pipelineStage}
                onChange={(e) => handleFilterChange('pipelineStage', e.target.value)}
                disabled={loadingOptions}
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {loadingOptions ? (
                  <option value="">Loading stages...</option>
                ) : (
                  pipelineStageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                  ))
                )}
              </select>
            </div>

            {/* Service Type Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Service Type</label>
              <select
                value={filters.serviceType}
                onChange={(e) => handleFilterChange('serviceType', e.target.value)}
                disabled={loadingOptions}
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {loadingOptions ? (
                  <option value="">Loading services...</option>
                ) : (
                  serviceTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                  ))
                )}
              </select>
            </div>

            {/* Customer Tier Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Customer Tier</label>
              <select
                value={filters.customerTier}
                onChange={(e) => handleFilterChange('customerTier', e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {customerTierOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Customer Type Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Customer Type</label>
              <select
                value={filters.customerType}
                onChange={(e) => handleFilterChange('customerType', e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {customerTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Date Range</label>
              <select
                value={filters.dateRange}
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {dateRangeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Lead Source Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Lead Source</label>
              <select
                value={filters.leadSource}
                onChange={(e) => handleFilterChange('leadSource', e.target.value)}
                disabled={loadingOptions}
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {loadingOptions ? (
                  <option value="">Loading sources...</option>
                ) : (
                  leadSourceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Location Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Location</label>
              <Input
                type="text"
                placeholder="Enter location..."
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                className="w-full"
              />
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center space-x-2 flex-wrap">
                <span className="text-sm text-text-muted">Active filters:</span>
                {Object.entries(filters).map(([key, value]) => {
                  if (!value) return null;
                  
                  const getFilterLabel = (key, value) => {
                    switch (key) {
                      case 'status':
                        return statusOptions.find(opt => opt.value === value)?.label || value;
                      case 'serviceType':
                        return serviceTypeOptions.find(opt => opt.value === value)?.label || value;
                      case 'customerTier':
                        return customerTierOptions.find(opt => opt.value === value)?.label || value;
                      case 'pipelineStage':
                        return pipelineStageOptions.find(opt => opt.value === value)?.label || value;
                      case 'customerType':
                        return customerTypeOptions.find(opt => opt.value === value)?.label || value;
                      case 'dateRange':
                        return dateRangeOptions.find(opt => opt.value === value)?.label || value;
                      case 'leadSource':
                        return leadSourceOptions.find(opt => opt.value === value)?.label || value;
                      default:
                        return value;
                    }
                  };

                  return (
                    <span
                      key={key}
                      className="inline-flex items-center space-x-1 bg-primary-50 text-primary px-2 py-1 rounded-full text-xs"
                    >
                      <span>{getFilterLabel(key, value)}</span>
                      <button
                        onClick={() => handleFilterChange(key, '')}
                        className="hover:bg-primary-100 rounded-full p-0.5"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerFilters;